import { useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { VideoCamera, Sparkle, Spinner, FilmStrip, Smiley, MusicNote } from "@phosphor-icons/react";
import { toast } from "sonner";

const CAMERA_SIMPLE = ["More cinematic", "More exciting", "More emotional", "More playful"];
const CAMERA_ADV = ["Close-up", "Medium", "Wide", "POV", "Low angle", "High angle", "Tracking", "Pan", "Tilt", "Dolly", "Push-in", "Pull-out", "Slow motion"];

export default function AIDirector() {
  const [dir, setDir] = useState("");
  const [busy, setBusy] = useState(false);
  const [structured, setStructured] = useState(null);
  const [mode, setMode] = useState("simple");

  const direct = async () => {
    if (!dir.trim()) { toast.error("Enter a direction"); return; }
    setBusy(true); setStructured(null);
    try {
      const { data } = await api.post("/ai/ask", { text:
        `You are an animation director. Convert this scene direction into structured JSON with keys character_emotion, character_movement, camera_movement, timing, lighting, voice_emotion, music, sfx. Direction: ${dir}. In your reply field, put a short confirmation, and list the structured items in preview_actions as "key: value".` });
      setStructured(data);
      toast.success("Direction structured");
    } catch { toast.error("Director failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader icon={VideoCamera} title="AI Director"
        subtitle="Give natural-language direction for any scene. Studio AI converts it into structured direction: emotion, movement, camera, timing, lighting, voice, music and SFX." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Panel>
            <textarea data-testid="director-input" value={dir} onChange={(e) => setDir(e.target.value)} rows={3}
              placeholder="e.g. The cart skids toward a closed gate; the horse pivots while the wizard casts the wrong spell. Track the action continuously."
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 resize-none" />
            <button data-testid="director-run" onClick={direct} disabled={busy}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
              {busy ? <Spinner size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" />} Direct Scene
            </button>
          </Panel>

          {structured && (
            <Panel>
              <div className="text-sm text-zinc-400 mb-3">Structured direction</div>
              <p className="text-sm text-white mb-3">{structured.reply}</p>
              <div className="space-y-2">
                {(structured.preview_actions || []).map((a, i) => (
                  <div key={i} className="rounded-lg border border-white/8 bg-black/20 px-3 py-2 font-mono text-xs text-zinc-200">{a}</div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                {["Preview", "Generate", "Regenerate", "Compare"].map((b) => (
                  <button key={b} onClick={() => toast(`${b}: Phase 2 (video provider).`)} className="rounded-full border border-white/10 bg-[#1C1C1C] px-3 py-1.5 text-xs text-white hover:border-white/30 transition-colors">{b}</button>
                ))}
              </div>
            </Panel>
          )}
        </div>

        <div className="space-y-5">
          <Panel>
            <div className="flex items-center gap-2 mb-3">
              <FilmStrip size={16} weight="duotone" className="text-[#00E5FF]" />
              <span className="text-sm text-zinc-400">Camera controls</span>
              <div className="ml-auto flex rounded-full border border-white/10 overflow-hidden text-xs">
                {["simple", "advanced"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} className={`px-3 py-1 ${mode === m ? "bg-white/10 text-white" : "text-zinc-500"}`}>{m}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(mode === "simple" ? CAMERA_SIMPLE : CAMERA_ADV).map((c) => (
                <button key={c} onClick={() => setDir((d) => (d ? d + " " : "") + c)} className="rounded-full border border-white/10 bg-[#1C1C1C] px-3 py-1.5 text-xs text-zinc-300 hover:border-[#00E5FF]/40 transition-colors">{c}</button>
              ))}
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Smiley size={16} weight="duotone" className="text-[#FF007F]" /> Emotion timeline</div>
            <div className="space-y-2">
              {[["0–1s", "Surprise", "#FFB800"], ["1–3s", "Curiosity", "#00E5FF"], ["3–6s", "Excitement", "#FF007F"]].map(([t, e, col]) => (
                <div key={t} className="flex items-center gap-3">
                  <span className="font-mono text-[11px] text-zinc-500 w-12">{t}</span>
                  <div className="flex-1 h-2 rounded-full" style={{ background: col, opacity: 0.5 }} />
                  <span className="text-xs text-white w-20 text-right">{e}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500"><MusicNote size={13} weight="duotone" /> Applies to face, body, voice & timing where supported.</div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
